import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { getFirebaseClients } from '../firebase';
import { toIsoString } from '../schemas/common';
import { parseDocs } from './parseDocs';
import {
  reservationCreateSchema,
  reservationSchema,
  type Reservation,
  type ReservationCreate,
  type ReservationStatus,
} from '../schemas/reservation';

const COLLECTION = 'reservations';

function db() {
  return getFirebaseClients().firestore;
}

function parse(raw: unknown, id: string): Reservation {
  const data = raw as Record<string, unknown>;
  return reservationSchema.parse({
    ...data,
    id,
    createdAt: toIsoString(data.createdAt),
    updatedAt: toIsoString(data.updatedAt),
  });
}

/** Public: create a pre-registration. `status`/`createdAt` are set here so the
 *  security rule (status == 'pending', createdAt == request.time) is satisfied. */
export async function createReservation(input: ReservationCreate): Promise<string> {
  const parsed = reservationCreateSchema.parse(input);
  const profile = { ...parsed.profile, email: parsed.profile.email.trim().toLowerCase() };
  const ref = await addDoc(collection(db(), COLLECTION), {
    ...parsed,
    profile,
    status: 'pending' as ReservationStatus,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

/** Admin: every reservation, newest first. */
export async function listReservations(): Promise<Reservation[]> {
  const snap = await getDocs(collection(db(), COLLECTION));
  return parseDocs(snap.docs, parse, COLLECTION).sort((a, b) =>
    (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
  );
}

export async function updateReservationStatus(
  id: string,
  status: ReservationStatus,
): Promise<void> {
  await updateDoc(doc(db(), COLLECTION, id), { status, updatedAt: serverTimestamp() });
}

export async function deleteReservation(id: string): Promise<void> {
  await deleteDoc(doc(db(), COLLECTION, id));
}
